import React, { useEffect, useMemo, useState } from "react";
import { calendarApi, entitiesApi, projectsApi, contactsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Wand2, Settings2 } from "lucide-react";
import EventDialog from "@/components/EventDialog";
import UniversalQuickAdd from "@/components/UniversalQuickAdd";
import { toast } from "sonner";

const EVENT_COLORS = {
  meeting: "#3B82F6", deadline: "#EF4444", launch: "#EC4899",
  production: "#F59E0B", payment: "#10B981", followup: "#94A3B8", milestone: "#A855F7",
};

function draftToEventPreset(rec) {
  const f = rec.fields || {};
  return {
    entity_id: f.entity_id || "",
    project_id: f.project_id || "",
    contact_id: f.contact_id || "",
    title: f.title || "",
    description: f.description || "",
    event_type: f.event_type || "meeting",
    starts_at: f.starts_at || "",
    ends_at: f.ends_at || "",
    location: f.location || "",
  };
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [dlg, setDlg] = useState({ open: false, event: null, defaultEntity: null });
  const [quickOpen, setQuickOpen] = useState(false);

  const load = async () => {
    const [ev, e, p, c] = await Promise.all([calendarApi.list(), entitiesApi.list(), projectsApi.list(), contactsApi.list()]);
    setEvents(ev.data); setEntities(e.data); setProjects(p.data); setContacts(c.data);
  };
  useEffect(() => { load(); }, []);

  const entityById = useMemo(() => Object.fromEntries(entities.map(e => [e.id, e])), [entities]);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startDay = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach(e => {
      const d = new Date(e.starts_at);
      const key = d.toISOString().slice(0,10);
      m[key] = m[key] || []; m[key].push(e);
    });
    return m;
  }, [events]);

  const monthLabel = cursor.toLocaleString("default", { month: "long", year: "numeric" });
  const today = new Date().toISOString().slice(0,10);

  const handleEditDetails = (rec) => {
    if (rec.type !== "calendar") { toast.error("Advanced form only supports events from this section"); return; }
    setDlg({ open: true, event: draftToEventPreset(rec), defaultEntity: rec.fields.entity_id });
  };

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="calendar-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow mb-2">Global Schedule</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Calendar</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-1">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 rounded hover:bg-white/10" data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></button>
            <div className="px-3 text-sm font-medium min-w-[140px] text-center">{monthLabel}</div>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 rounded hover:bg-white/10" data-testid="cal-next"><ChevronRight className="w-4 h-4"/></button>
          </div>
          <Button onClick={() => setQuickOpen(true)} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="new-event-btn"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
          <Button variant="ghost" onClick={() => setDlg({ open: true, event: null, defaultEntity: entities[0]?.id })} className="text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid="new-event-advanced-btn"><Settings2 className="w-3.5 h-3.5 mr-1.5"/>Advanced form</Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#151A23] overflow-hidden">
        <div className="grid grid-cols-7 border-b border-white/10">
          {"Sun,Mon,Tue,Wed,Thu,Fri,Sat".split(",").map(d => (
            <div key={d} className="px-3 py-2 text-eyebrow border-r border-white/10 last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d?.toISOString().slice(0,10);
            const dayEvents = (key && eventsByDay[key]) || [];
            const isToday = key === today;
            return (
              <div key={i} className={`min-h-[110px] p-2 border-r border-b border-white/10 ${!d ? "bg-white/[0.02]" : ""}`}>
                {d && (
                  <>
                    <div className={`text-xs mb-1.5 ${isToday ? "text-white font-semibold" : "text-white/50"}`}>{d.getDate()}{isToday && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-400"/>}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0,3).map(e => (
                        <button
                          key={e.id}
                          onClick={() => setDlg({ open: true, event: e })}
                          className="w-full text-left text-[11px] px-1.5 py-1 rounded truncate hover:brightness-125"
                          style={{ background: `${EVENT_COLORS[e.event_type] || "#3B82F6"}22`, color: EVENT_COLORS[e.event_type] || "#3B82F6", borderLeft: `2px solid ${EVENT_COLORS[e.event_type] || "#3B82F6"}` }}
                          title={`${e.title} — ${entityById[e.entity_id]?.name || ""}`}
                          data-testid={`cal-event-${e.id}`}
                        >
                          {e.all_day
                            ? e.title
                            : `${new Date(e.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ${e.title}`}
                        </button>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[10px] text-white/40 pl-1">+{dayEvents.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <EventDialog
        open={dlg.open}
        setOpen={(v) => setDlg({ ...dlg, open: v })}
        event={dlg.event}
        entityId={dlg.event?.entity_id || dlg.defaultEntity || entities[0]?.id}
        projects={projects.filter(p => p.entity_id === (dlg.event?.entity_id || dlg.defaultEntity || entities[0]?.id))}
        contacts={contacts}
        onSaved={() => { setDlg({ open: false, event: null }); load(); }}
      />
      <UniversalQuickAdd open={quickOpen} setOpen={setQuickOpen} section="calendar" onCreated={load} onEditDetails={handleEditDetails}/>
    </div>
  );
}
